import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Clock, CheckCircle, X } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const content = {
  pt: {
    trial: {
      title: "Período de avaliação",
      daysLeft: "dias restantes",
      lastDay: "Último dia de avaliação!",
      expired: "Período de avaliação expirado",
      upgrade: "Escolher plano"
    },
    subscription: {
      active: "Plano ativo",
      expires: "Expira em",
      days: "dias"
    }
  },
  en: {
    trial: {
      title: "Trial period",
      daysLeft: "days left",
      lastDay: "Last day of trial!",
      expired: "Trial period expired",
      upgrade: "Choose plan"
    },
    subscription: {
      active: "Active plan",
      expires: "Expires in",
      days: "days"
    }
  }
};

export default function SubscriptionBanner({ slug }) {
  const { i18n } = useTranslation();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  const currentLang = i18n.language || 'pt';
  const t = content[currentLang] || content.pt;

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/empresas/${slug}/subscription-status`);
        setStatus(response.data);
      } catch (error) {
        console.error("Error fetching subscription status:", error);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchStatus();
    }
  }, [slug]);

  if (loading || dismissed || !status) return null;

  // Don't show banner if subscription is active with more than 7 days left
  if (status.subscription_active && status.subscription_days_left > 7) return null;

  // Determine banner style based on status
  let bannerClass = "";
  let icon = null;
  let message = "";
  let showUpgrade = false;

  if (status.status === "expired") {
    bannerClass = "bg-red-500";
    icon = <AlertTriangle className="w-5 h-5" />;
    message = t.trial.expired;
    showUpgrade = true;
  } else if (status.status === "trial") {
    if (status.trial_days_left <= 1) {
      bannerClass = "bg-orange-500";
      icon = <AlertTriangle className="w-5 h-5" />;
      message = t.trial.lastDay;
      showUpgrade = true;
    } else if (status.trial_days_left <= 3) {
      bannerClass = "bg-yellow-500 text-yellow-900";
      icon = <Clock className="w-5 h-5" />;
      message = `${t.trial.title}: ${status.trial_days_left} ${t.trial.daysLeft}`;
      showUpgrade = true;
    } else {
      bannerClass = "bg-blue-500";
      icon = <Clock className="w-5 h-5" />;
      message = `${t.trial.title}: ${status.trial_days_left} ${t.trial.daysLeft}`;
    }
  } else if (status.status === "subscribed" && status.subscription_days_left <= 7) {
    bannerClass = "bg-yellow-500 text-yellow-900";
    icon = <CheckCircle className="w-5 h-5" />;
    message = `${t.subscription.active}: ${status.plano} • ${t.subscription.expires} ${status.subscription_days_left} ${t.subscription.days}`;
  }

  if (!message) return null;

  return (
    <div className={`${bannerClass} text-white px-4 py-2`} data-testid="subscription-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium">{message}</span>
        </div>
        <div className="flex items-center gap-3">
          {showUpgrade && (
            <Link 
              to="/#pricing" 
              className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full text-sm font-medium transition-colors"
            >
              {t.trial.upgrade}
            </Link>
          )}
          <button 
            onClick={() => setDismissed(true)}
            className="hover:bg-white/20 p-1 rounded-full transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
